/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type ReactElement, useMemo, useContext } from 'react';
import { ErrorHashAndTraceId } from '@jira/platform__error-hash-component/src/error-hash-and-trace-id/ErrorHashAndTraceId.tsx';
import ErrorImageComponent from '@jira/illustrations__illustrations/src/ui/adg4/brand/spots/error/components/error/index.tsx';
import SearchErrorImageComponent from '@jira/illustrations__illustrations/src/ui/adg4/brand/spots/error/components/search-error/index.tsx';
import { getAkEmptyStateRenderImageFn } from '@jira/illustrations__illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';
import UFOInteractionIDContext from '@atlaskit/react-ufo/interaction-id-context';
import {
	addError,
	addErrorToAll,
	getActiveInteraction,
	removeHoldByID,
} from '@atlaskit/react-ufo/interaction-metrics';
import EmptyState from '@atlaskit/empty-state/empty-state';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { getActiveTrace } from '@atlaskit/react-ufo/experience-trace-id-context';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src/DocumentTitle.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { Link } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';

const notFoundDocumentTitleJsm = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.not-found-document-title-jsm',
	defaultMessage: 'Project not found - Service project',
	description: 'The document title to show when there is an error',
});

const notFoundDocumentTitleJsmGalaxia = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.not-found-document-title-jsm-galaxia',
	defaultMessage: 'Space not found - Service space',
	description: 'The document title to show when there is an error',
});

const notFoundTitle = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.not-found-title',
	defaultMessage: "This project isn't available",
	description: "Title of the error message when the project can't be found",
});

const notFoundTitleGalaxia = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.not-found-title-galaxia',
	defaultMessage: "This space isn't available",
	description: "Title of the error message when the space can't be found",
});

const notFoundDescription = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.not-found-description',
	defaultMessage: 'It may have been deleted or your permissions may have changed',
	description: "Description of the error message when the project can't be found",
});

const notFoundAction = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.not-found-action',
	defaultMessage: 'View a list of all projects',
	description: "Call to action when the project can't be found",
});

const notFoundActionGalaxia = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.not-found-action-galaxia',
	defaultMessage: 'View a list of all spaces',
	description: "Call to action when the space can't be found",
});

const genericDocumentTitleJsm = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.generic-document-title-jsm',
	defaultMessage: 'Error - Service project',
	description: 'The document title to show when there is an error',
});

const genericDocumentTitleJsmGalaxia = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.generic-document-title-jsm-galaxia',
	defaultMessage: 'Error - Service space',
	description: 'The document title to show when there is an error',
});

const genericErrorTitle = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.generic-error-title',
	defaultMessage: 'An error occurred',
	description: 'Title of the error message for a generic error',
});

const genericErrorDescription = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.generic-error-description',
	defaultMessage: 'An error occurred while loading this page',
	description: 'Description of the error message for a generic error',
});

const serviceHubNotFoundDocumentTitleJsm = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.servicehub-not-found-document-title-jsm',
	defaultMessage: 'Service hub not found - Service project',
	description: 'The document title to show when there is an error',
});

const serviceHubNotFoundDocumentTitleJsmGalaxia = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.servicehub-not-found-document-title-jsm-galaxia',
	defaultMessage: 'Service hub not found - Service space',
	description: 'The document title to show when there is an error',
});

const serviceHubNotFoundTitle = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.servicehub-not-found-title',
	defaultMessage: "This service hub isn't available",
	description: "Title of the error message when the Service hub can't be found",
});

const serviceHubNotFoundDescription = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.servicehub-not-found-description',
	defaultMessage: 'It may have been deleted or your permissions may have changed',
	description: "Description of the error message when the customer experience can't be found",
});

const serviceHubNotFoundAction = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.servicehub-not-found-action',
	defaultMessage: 'View a list of all service hubs',
	description: "Call to action when the service hub can't be found",
});

const customerHubNotFoundDocumentTitleJsm = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.customer-hub-not-found-document-title-jsm',
	defaultMessage: 'Customer experience not found - Service project',
	description: 'The document title to show when there is an error',
});

const customerHubNotFoundDocumentTitleJsmGalaxia = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.customer-hub-not-found-document-title-jsm-galaxia',
	defaultMessage: 'Customer experience not found - Service space',
	description: 'The document title to show when there is an error',
});

const customerHubNotFoundTitle = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.customer-hub-not-found-title',
	defaultMessage: "This customer experience isn't available",
	description: "Title of the error message when the Customer experience can't be found",
});

const customerHubNotFoundDescription = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.customer-hub-not-found-description',
	defaultMessage: 'It might have been deleted or your permissions may have changed.',
	description: "Description of the error message when the customer experience can't be found",
});

const customerHubNotFoundAction = defineMessage({
	id: 'servicedesk-spa-commons.common.utils.errors.customer-hub-not-found-action',
	defaultMessage: 'View all customer experiences',
	description: "Call to action when the customer experiences can't be found",
});

export const ProjectNotFound = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const projectsPath = '/jira/projects';

	return (
		<>
			<DocumentTitle
				title={formatMessage(
					fg('itsm-project-renaming') ? notFoundDocumentTitleJsmGalaxia : notFoundDocumentTitleJsm,
				)}
			/>
			<EmptyState
				renderImage={getAkEmptyStateRenderImageFn(SearchErrorImageComponent)}
				maxImageWidth={120}
				header={formatMessage(fg('itsm-project-renaming') ? notFoundTitleGalaxia : notFoundTitle)}
				description={formatMessage(notFoundDescription)}
				primaryAction={
					<Link to={projectsPath}>
						{formatMessage(fg('itsm-project-renaming') ? notFoundActionGalaxia : notFoundAction)}
					</Link>
				}
			/>
		</>
	);
};

export const ServiceHubNotFound = (): React.JSX.Element => {
	const projectsPath = '/jira/projects';
	const customerHubsPath = '/jira/helpcenter';

	const { formatMessage } = useIntl();

	const getDocumentTitleMessage = () => {
		if (fg('jcs_project_type_m3')) {
			return fg('itsm-project-renaming')
				? customerHubNotFoundDocumentTitleJsmGalaxia
				: customerHubNotFoundDocumentTitleJsm;
		}
		return fg('itsm-project-renaming')
			? serviceHubNotFoundDocumentTitleJsmGalaxia
			: serviceHubNotFoundDocumentTitleJsm;
	};

	return (
		<>
			<DocumentTitle title={formatMessage(getDocumentTitleMessage())} />
			<EmptyState
				renderImage={getAkEmptyStateRenderImageFn(SearchErrorImageComponent)}
				maxImageWidth={120}
				header={formatMessage(
					fg('jcs_project_type_m3') ? customerHubNotFoundTitle : serviceHubNotFoundTitle,
				)}
				description={formatMessage(
					fg('jcs_project_type_m3')
						? customerHubNotFoundDescription
						: serviceHubNotFoundDescription,
				)}
				primaryAction={
					<Link to={fg('jcs_project_type_m3') ? customerHubsPath : projectsPath}>
						{formatMessage(
							fg('jcs_project_type_m3') ? customerHubNotFoundAction : serviceHubNotFoundAction,
						)}
					</Link>
				}
			/>
		</>
	);
};

export const GenericError = ({
	interactionForcedError,
}: {
	interactionForcedError?: boolean;
}): React.JSX.Element => {
	const interactionId = useContext(UFOInteractionIDContext).current;
	const labelStack = useContext(UFOInteractionContext)?.labelStack;

	const traceId = useMemo(() => {
		const traceContext = getActiveTrace();

		return traceContext?.traceId;
	}, []);

	const { formatMessage } = useIntl();
	let description: ReactElement | string = formatMessage(genericErrorDescription);

	if (traceId) {
		const error = new Error('An error occurred while loading this page');
		description = (
			<>
				<div>{description}</div>
				<Box xcss={styles}>
					<ErrorHashAndTraceId error={error} traceId={traceId} />
				</Box>
			</>
		);

		const name = 'serviceDeskGenericError';
		if (interactionId) {
			addError(
				interactionId,
				name,
				labelStack ?? null,
				error.name,
				error.message,
				error.stack,
				interactionForcedError,
			);
			// NOTE: to account for awaiting BM3 TTI in UFO code, however we don't need to wait for BM3 TTI when there is an error
			// https://atlassian.slack.com/archives/C01AY8132S3/p1736380314352749?thread_ts=1736377740.484469&cid=C01AY8132S3
			const activeInteraction = getActiveInteraction();
			activeInteraction?.holdActive.forEach((_, holdId) => {
				removeHoldByID(activeInteraction.id, holdId);
			});
		} else {
			addErrorToAll(name, labelStack ?? null, error.name, error.message, error.stack);
		}
	}
	return (
		<>
			<DocumentTitle
				title={formatMessage(
					fg('itsm-project-renaming') ? genericDocumentTitleJsmGalaxia : genericDocumentTitleJsm,
				)}
			/>
			<EmptyState
				renderImage={getAkEmptyStateRenderImageFn(ErrorImageComponent)}
				maxImageWidth={120}
				header={formatMessage(genericErrorTitle)}
				description={description}
			/>
		</>
	);
};

const styles = xcss({
	marginTop: 'space.100',
});
